import '../aktionen/module'; // Seiteneffekt: registriert alle Aktionstyp-Module auf aktionsRegistry
import type { EmailAnalyse } from './analyseApi';
import { setzeCockpitStatus } from './analyseApi';
import { analyseZuTicketVorlage } from './ticketVorlage';
import { erstelleTicket } from '../tickets/erstelleTicket';
import { verknuepfeTicketMitEmail } from './ticketLink';
import { aktionsRegistry } from '../aktionen/registry';
import { speichereAktionen } from '../aktionen/api';
import { baueAktionsKontext } from '../aktionen/kontext';
import { rueckfragen, vorgangsTicket, type AngabeZeile } from '../vorgaenge/start';
import type { Vorgangsart } from '../vorgaenge/vorgangsarten';
import { supabase } from '../supabase/client';

// Struktur wie in ticketVorlage.ts (dort nicht exportiert) — E-Mail-Kontext für die Vorlagen-Ableitung.
type EmailKontext = {
  betreff?: string | null;
  plainText?: string | null;
  objekt_id?: string | null;
  mieter_id?: string | null;
  absender?: string | null;
  datum?: string | null;
};

type VorschlagInfo = { name: string; telefon: string | null } | null;

type Opts = { kategorien?: { id: string; parent_id: string | null }[]; schwelle?: number };

/** #489: Vorgang starten statt einfachem Ticket */
export type VorgangStart = { art: Vorgangsart; angaben: AngabeZeile[]; checkliste: string | null };

// Dünner Orchestrator (Konvention: nicht unit-getestet — die Bausteine sind es).
// Bündelt den kompletten 1-Klick-Flow "Vorschlag freigeben -> Ticket": Vorlage ableiten,
// Ticket anlegen, mit Quell-E-Mail verknüpfen, Aktions-Entwürfe erzeugen (best effort) und
// den Cockpit-Status der Analyse auf 'freigegeben' setzen (best effort).
export async function erstelleTicketAusAnalyse(
  analyse: EmailAnalyse,
  email: EmailKontext,
  vorschlag: VorschlagInfo,
  opts: Opts | undefined,
  erstelltVon: string | null,
  vorgang?: VorgangStart,
): Promise<{ id: string; ticket_nummer: string }> {
  const basis = analyseZuTicketVorlage(analyse, email, vorschlag, opts);
  const vorlage = vorgang ? vorgangsTicket(basis, vorgang.art, vorgang.angaben, vorgang.checkliste, new Date()) : basis;
  const ticket = await erstelleTicket(vorlage, erstelltVon);
  await verknuepfeTicketMitEmail(ticket.id, analyse.email_id);

  try {
    const ctx = await baueAktionsKontext({
      id: ticket.id,
      titel: vorlage.titel,
      ticket_nummer: ticket.ticket_nummer,
      objekt_id: vorlage.objekt_id ?? null,
      mieter_id: vorlage.mieter_id ?? null,
      einheit_id: vorlage.einheit_id ?? null,
      kategorie_id: vorlage.kategorie_id ?? null,
      prioritaet: vorlage.prioritaet ?? null,
      absender_typ: vorlage.absender_typ ?? null,
      erkannte_aufgabe: vorlage.erkannte_aufgabe ?? null,
      naechster_schritt: vorlage.naechster_schritt ?? null,
      dienstleister_vorschlag_id: vorlage.dienstleister_vorschlag_id ?? null,
      ki_confidence: vorlage.ki_confidence ?? null,
      ist_rechtlich_sensibel: vorlage.ist_rechtlich_sensibel ?? false,
      bearbeiter_vorschlag_id: vorlage.bearbeiter_vorschlag_id ?? null,
      bearbeiter_vorschlag_typ: vorlage.bearbeiter_vorschlag_typ ?? null,
    });
    if (vorgang) ctx.vorgang = { antwortVorlageId: vorgang.art.antwort_vorlage_id, rueckfragen: rueckfragen(vorgang.angaben) };
    // Freigabestufe „Nur Vorschlag“: keine Antwort an den Absender vorbereiten
    const entwuerfe = aktionsRegistry.erzeugeEntwuerfe(ctx)
      .filter(e => !(vorgang?.art.freigabe_stufe === 'vorschlag' && e.typ === 'antwort_absender'));
    await speichereAktionen(entwuerfe, ticket.id, erstelltVon);
  } catch (e) {
    console.warn('Aktions-Entwürfe erzeugen/speichern fehlgeschlagen', e);
  }

  if (vorgang) {
    await supabase.from('ticket_historie').insert({
      ticket_id: ticket.id, benutzer_id: erstelltVon, aktion: 'vorgang_gestartet', neuer_wert: `Vorgang „${vorgang.art.name}“ gestartet`,
    });
  }

  try {
    await setzeCockpitStatus(analyse.id, 'freigegeben');
  } catch (e) {
    console.warn('Cockpit-Status setzen fehlgeschlagen', e);
  }

  return { id: ticket.id, ticket_nummer: ticket.ticket_nummer };
}
